import {DateFormatter} from '@/services/date-helpers';
import {v4} from 'uuid';

export interface DateRange {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export interface POIOption {
  id: string;
  type: string;
  name: string;
  lat: number;
  lng: number;
}

export interface TripPartInfo {
  id: string;
  origin: POIOption | null;
  destination: POIOption | null;
  destinationImage?: string;
  dateRange: DateRange;
  lockedOrigin: boolean;
  lockedDestination: boolean;
  minDate: Date | null;
  invalidDate: boolean;
}

const defaultOrigin: POIOption = {
  name: 'New York, New York, United States',
  type: 'CITY',
  id: 'dcef5298-7673-3b18-ad71-cde7c166c9f2',
  lat: 40.6943,
  lng: -73.9249,
};

const getDefaultDateRange = (): DateRange => {
  const startDate = new Date();
  const endDate = new Date();

  startDate.setDate(startDate.getDate() + 1);
  endDate.setDate(endDate.getDate() + 8);

  return {startDate, endDate};
};

export const createTripPartInfo = (): TripPartInfo => ({
  id: v4(),
  origin: defaultOrigin,
  destination: null,
  dateRange: getDefaultDateRange(),
  lockedOrigin: false,
  lockedDestination: false,
  minDate: null,
  invalidDate: false,
});

export const isTripInfoComplete = (tripInfo: TripPartInfo) => {
  return (
    tripInfo.origin &&
    tripInfo.destination &&
    tripInfo.origin.id !== tripInfo.destination.id &&
    tripInfo.dateRange &&
    tripInfo.dateRange.startDate
  );
};

export const isAllTripsInfoComplete = (tripInfoArr: TripPartInfo[]): boolean => {
  if (!tripInfoArr || tripInfoArr.length === 0) {
    return false;
  }

  for (let i = 0; i < tripInfoArr.length; i++) {
    if (!isTripInfoComplete(tripInfoArr[i])) {
      return false;
    }
  }

  return true;
};

export const setOriginsInMultiCityTrip = (tripInfoArr: TripPartInfo[]) => {
  // first row: Origin and Dest are editable.
  tripInfoArr[0].lockedOrigin = false;
  tripInfoArr[0].lockedDestination = false;

  // rows after first:
  // - Origin is locked and set to prev row Destination.
  // - Destination is editable
  for (let i = 1; i < tripInfoArr.length; i++) {
    tripInfoArr[i].origin = tripInfoArr[i - 1].destination;
    tripInfoArr[i].lockedOrigin = true;
    tripInfoArr[i].lockedDestination = false;
  }
};

// Makes sense only for Multi city
export const setMinDatesAndValidateDates = (tripInfoArr: TripPartInfo[]) => {
  // minStartDate will start from first date
  let minStartDate = tripInfoArr[0].dateRange.startDate ? tripInfoArr[0].dateRange.startDate : new Date();
  tripInfoArr[0].invalidDate = false;

  for (let i = 1; i < tripInfoArr.length; i++) {
    const prevStartDate = tripInfoArr[i - 1].dateRange.startDate;
    const currStartDate = tripInfoArr[i].dateRange.startDate;
    // if previous date exists and it's bigger then minStartDate - update minStartDate to this date
    if (prevStartDate && minStartDate < prevStartDate) {
      minStartDate = new Date(prevStartDate);
    }

    // set minDate and reset invalidDate flag
    tripInfoArr[i].minDate = new Date(minStartDate);
    tripInfoArr[i].invalidDate = false;

    // if current date is less then minStartDate - set invalidDate flag
    if (currStartDate && currStartDate < minStartDate) {
      tripInfoArr[i].invalidDate = true;
    }
  }
};

export const getTripDateRangeStr = (tripDestinations: TripPartInfo[]) => {
  const startDate = tripDestinations[0].dateRange.startDate;
  const endDate = tripDestinations[tripDestinations.length - 1].dateRange.endDate;

  return DateFormatter.formatShortDateRange(startDate, endDate);
};

export const cityShortName = (cityName: string): string => {
  return cityName.split(',')[0];
};
